// src/components/aiinsight/ReportCanvas.jsx
// Full-page canvas-based report editor.
// Activated when user clicks "Generate Report" after multi-selecting pinned items.
// Features: editable text, resizable charts, section reorder, AI enhancement, export.

import React, {
    useState, useRef, useCallback, useEffect, useMemo,
} from 'react';
import {
    Box, Button, Typography, IconButton, Tooltip, TextField, Divider,
    Chip, CircularProgress, Popover, Alert, Snackbar, Menu, MenuItem,
    ListItemIcon, ListItemText, Fade, Backdrop, LinearProgress, TextareaAutosize
} from '@mui/material';
import { flushSync } from 'react-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import PreviewIcon from '@mui/icons-material/Preview';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import AlignHorizontalLeftIcon from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import ExcelJS from 'exceljs';
import AiChartRenderer from './AiChartRenderer';
import { markdownToHtml, isRawMarkdown } from './narrativeUtils';
import { apiFetch } from '../../api';

// ── helpers ──────────────────────────────────────────────────────────────────
const doubleRAF = () => new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

const stripTags = (html = '') => html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();

const buildSectionId = (item) => `canvas-section-${item.id}`;

// ── Section drag-reorder hook ─────────────────────────────────────────────────
function useSectionDrag(onReorder) {
    const dragSrc = useRef(null);
    const dragOver = useRef(null);

    const onDragStart = useCallback((idx) => {
        dragSrc.current = idx;
        dragOver.current = idx;
    }, []);

    const onDragEnter = useCallback((idx) => {
        dragOver.current = idx;
    }, []);

    const onDragEnd = useCallback(() => {
        const from = dragSrc.current;
        const to = dragOver.current;
        if (from !== null && to !== null && from !== to) onReorder(from, to);
        dragSrc.current = null;
        dragOver.current = null;
    }, [onReorder]);

    return { onDragStart, onDragEnter, onDragEnd };
}

// ── Inline rich-text toolbar ──────────────────────────────────────────────────
const InlineToolbar = ({ editorRef }) => {
    const exec = (cmd, val) => {
        editorRef.current?.focus();
        document.execCommand(cmd, false, val ?? null);
    };
    const btn = (title, icon, cmd, val) => (
        <Tooltip title={title} key={cmd + (val || '')}>
            <IconButton
                size="small"
                onMouseDown={e => { e.preventDefault(); exec(cmd, val); }}
                sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: '#6366f1' } }}
            >
                {icon}
            </IconButton>
        </Tooltip>
    );
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap',
            px: 1, py: 0.5,
            bgcolor: t => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f8fafc',
            borderBottom: '1px solid', borderColor: 'divider',
        }}>
            {btn('Bold', <FormatBoldIcon sx={{ fontSize: 16 }} />, 'bold')}
            {btn('Italic', <FormatItalicIcon sx={{ fontSize: 16 }} />, 'italic')}
            {btn('Underline', <FormatUnderlinedIcon sx={{ fontSize: 16 }} />, 'underline')}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {btn('Bullet list', <FormatListBulletedIcon sx={{ fontSize: 16 }} />, 'insertUnorderedList')}
            {btn('Numbered list', <FormatListNumberedIcon sx={{ fontSize: 16 }} />, 'insertOrderedList')}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {btn('Clear formatting', <FormatClearIcon sx={{ fontSize: 16 }} />, 'removeFormat')}
        </Box>
    );
};

// ── Single canvas section ─────────────────────────────────────────────────────
const CanvasSection = ({
    item, idx, totalCount,
    animationDisabled,
    onDragStart, onDragEnter, onDragEnd,
    onRemove, onUpdateNarrative, onUpdateTitle, onUpdateWidth,
    isEnhancing, isExporting,
}) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleVal, setTitleVal] = useState(item.title || '');
    const [chartAlign, setChartAlign] = useState('center'); // 'left' | 'center' | 'full'
    const [chartHeight, setChartHeight] = useState(260);
    const editorRef = useRef(null);
    const resizeRef = useRef(null);

    const isChart = !!item.viz_config_json;
    const narrativeHtml = useMemo(() => {
        const raw = item.narrative || '';
        if (!raw) return '';
        return isRawMarkdown(raw) ? markdownToHtml(raw) : raw;
    }, [item.narrative]);

    // Chart resize drag
    const startResize = useCallback((e) => {
        e.preventDefault();
        const startY = e.clientY;
        const startH = chartHeight;
        const onMove = (ev) => setChartHeight(Math.max(160, startH + (ev.clientY - startY)));
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [chartHeight]);

    const commitTitle = () => {
        setEditingTitle(false);
        if (titleVal.trim() && titleVal.trim() !== item.title) {
            onUpdateTitle(idx, titleVal.trim());
        }
    };

    const handleNarrativeBlur = () => {
        if (editorRef.current) {
            onUpdateNarrative(idx, editorRef.current.innerHTML);
        }
    };

    // Sync editor content when item changes (e.g., AI enhancement)
    useEffect(() => {
        if (editorRef.current && !isEnhancing) {
            const incoming = narrativeHtml;
            if (editorRef.current.innerHTML !== incoming) {
                editorRef.current.innerHTML = incoming;
            }
        }
    }, [narrativeHtml, isEnhancing]);

    const chartAlignStyle = {
        left: { display: 'flex', justifyContent: 'flex-start' },
        center: { display: 'flex', justifyContent: 'center' },
        full: { display: 'block', width: '100%' },
    }[chartAlign];

    return (
        <Box
            id={buildSectionId(item)}
            className="pdf-page-guard"
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragEnter={() => onDragEnter(idx)}
            onDragEnd={onDragEnd}
            onDragOver={e => e.preventDefault()}
            sx={{
                position: 'relative',
                mb: 0,
                '&:hover .section-controls': { opacity: 1 },
                transition: 'opacity 0.2s',
                opacity: isEnhancing ? 0.55 : 1,
            }}
        >
            {/* Section separator line */}
            {idx > 0 && (
                <Box sx={{ height: 1, bgcolor: '#e2e8f0', mb: 2.5, mt: 0.5 }} />
            )}

            {/* Top controls bar */}
            {!isExporting && (
                <Box
                    className="section-controls"
                    sx={{
                        position: 'absolute',
                        top: idx > 0 ? 16 : 0,
                        right: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        zIndex: 10,
                        bgcolor: t => t.palette.mode === 'dark' ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,0.95)',
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        px: 0.5,
                        py: 0.25,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                >
                    <Tooltip title="Drag to reorder">
                        <Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', px: 0.25, color: 'text.disabled', '&:active': { cursor: 'grabbing' } }}>
                            <DragIndicatorIcon sx={{ fontSize: 16 }} />
                        </Box>
                    </Tooltip>
                    <>
                        <Tooltip title="Half width (2 columns)">
                            <IconButton size="small" onClick={() => onUpdateWidth && onUpdateWidth(idx, 'half')}
                                sx={{ p: 0.4, color: item.layout_width === 'half' ? '#6366f1' : 'text.disabled' }}>
                                <Box sx={{ width: 12, height: 12, border: '2px solid currentColor', borderRight: '6px solid currentColor', borderRadius: 0.5 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Full width">
                            <IconButton size="small" onClick={() => onUpdateWidth && onUpdateWidth(idx, 'full')}
                                sx={{ p: 0.4, color: item.layout_width !== 'half' ? '#6366f1' : 'text.disabled' }}>
                                <Box sx={{ width: 12, height: 12, border: '2px solid currentColor', bgcolor: 'currentColor', borderRadius: 0.5 }} />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                    </>
                    {isChart && (
                        <>
                            <Tooltip title="Align left">
                                <IconButton size="small" onClick={() => setChartAlign('left')}
                                    sx={{ p: 0.4, color: chartAlign === 'left' ? '#6366f1' : 'text.disabled' }}>
                                    <AlignHorizontalLeftIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Align center">
                                <IconButton size="small" onClick={() => setChartAlign('center')}
                                    sx={{ p: 0.4, color: chartAlign === 'center' ? '#6366f1' : 'text.disabled' }}>
                                    <AlignHorizontalCenterIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Full width">
                                <IconButton size="small" onClick={() => setChartAlign('full')}
                                    sx={{ p: 0.4, color: chartAlign === 'full' ? '#6366f1' : 'text.disabled' }}>
                                    <OpenWithIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem />
                        </>
                    )}
                    <Tooltip title="Remove section">
                        <IconButton size="small" onClick={() => onRemove(idx)}
                            sx={{ p: 0.4, color: 'text.disabled', '&:hover': { color: '#f43f5e' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Section number + type chip */}
            {!isExporting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.5 }}>
                        {String(idx + 1).padStart(2, '0')}
                    </Typography>
                    <Chip
                        label={isChart ? 'Chart' : 'Insight'}
                        size="small"
                        sx={{
                            height: 16, fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                            bgcolor: isChart ? 'rgba(6,182,212,0.1)' : 'rgba(167,139,250,0.12)',
                            color: isChart ? '#06b6d4' : '#a78bfa',
                            border: 'none',
                        }}
                    />
                </Box>
            )}

            {/* Editable section title */}
            {editingTitle ? (
                <Box sx={{ mb: 1.5 }}>
                    <input
                        autoFocus
                        value={titleVal}
                        onChange={e => setTitleVal(e.target.value)}
                        onBlur={commitTitle}
                        onKeyDown={e => {
                            if (e.key === 'Enter') commitTitle();
                            if (e.key === 'Escape') { setTitleVal(item.title); setEditingTitle(false); }
                        }}
                        style={{
                            width: '100%', fontSize: 17, fontWeight: 700,
                            lineHeight: 1.3, color: 'inherit', fontFamily: 'inherit',
                            border: 'none', borderBottom: '2px solid #6366f1',
                            outline: 'none', background: 'transparent',
                            padding: '2px 0',
                        }}
                    />
                </Box>
            ) : (
                <Typography
                    onDoubleClick={() => { setTitleVal(item.title || ''); setEditingTitle(true); }}
                    title="Double-click to edit title"
                    sx={{
                        fontSize: 17, fontWeight: 700, color: '#0f172a',
                        lineHeight: 1.3, mb: 1.5, cursor: 'text',
                        letterSpacing: '-0.01em',
                        '&:hover': { color: '#1e293b' },
                        transition: 'color 0.15s',
                    }}
                >
                    {item.title || 'Untitled Section'}
                </Typography>
            )}

            {/* Chart */}
            {isChart && (
                <Box sx={{ mb: narrativeHtml ? 1.5 : 0 }}>
                    <Box sx={chartAlignStyle}>
                        <Box
                            data-ai-chart-wrapper="true"
                            sx={{
                                width: chartAlign === 'full' ? '100%' : chartAlign === 'center' ? '90%' : '75%',
                                position: 'relative',
                            }}
                        >
                            <AiChartRenderer
                                vizConfig={item.viz_config_json}
                                data={item.result_json || []}
                                height={chartHeight}
                                animationDisabled={animationDisabled}
                            />
                            {/* Resize handle */}
                            {!isExporting && (
                                <Box
                                    ref={resizeRef}
                                    onMouseDown={startResize}
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0, right: 0,
                                        width: 16, height: 16,
                                        cursor: 'se-resize',
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Cpath d='M9 1L1 9M9 5L5 9M9 9' stroke='%236366f1' stroke-opacity='0.5' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'bottom right',
                                        zIndex: 5,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                    {(!isExporting && item.result_json?.length > 0) && (
                        <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 0.75, textAlign: chartAlign === 'left' ? 'left' : 'center' }}>
                            {item.result_json.length.toLocaleString()} rows of data
                        </Typography>
                    )}
                </Box>
            )}

            {/* Narrative text (contentEditable) */}
            {!isChart && (
                <Box sx={{ position: 'relative', '&:focus-within .text-toolbar': { opacity: 1, pointerEvents: 'auto' } }}>
                    <Box
                        className="text-toolbar"
                        sx={{ opacity: 0, pointerEvents: 'none', transition: 'opacity 0.15s', borderRadius: '6px 6px 0 0', overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderBottom: 'none' }}
                    >
                        <InlineToolbar editorRef={editorRef} />
                    </Box>
                    <Box
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={handleNarrativeBlur}
                        dangerouslySetInnerHTML={{ __html: narrativeHtml }}
                        sx={{
                            fontSize: 14, lineHeight: 1.85, color: '#334155',
                            outline: 'none', minHeight: 60,
                            border: '1px solid transparent',
                            borderRadius: 1.5,
                            p: 1,
                            cursor: 'text',
                            transition: 'border-color 0.15s, background 0.15s',
                            '&:focus': {
                                borderColor: '#e2e8f0',
                                bgcolor: t => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa',
                                borderRadius: '0 0 6px 6px',
                            },
                            '& p': { margin: '0 0 8px 0' },
                            '& ul': { pl: 3, mt: 0.5, mb: 1, listStyleType: 'disc', listStylePosition: 'outside' },
                            '& ol': { pl: 3, mt: 0.5, mb: 1, listStyleType: 'decimal', listStylePosition: 'outside' },
                            '& li': { mb: '4px', display: 'list-item' },
                            '& strong, & b': { fontWeight: 700, color: '#1e293b' },
                            '& h2, & h3': { fontSize: '14px', fontWeight: 700, color: '#6366f1', mt: '12px', mb: '4px' },
                        }}
                    />
                </Box>
            )}

            {/* Inline chart narrative */}
            {isChart && narrativeHtml && (
                <Box
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={handleNarrativeBlur}
                    dangerouslySetInnerHTML={{ __html: narrativeHtml }}
                    sx={{
                        fontSize: 13, lineHeight: 1.75, color: '#475569',
                        outline: 'none', minHeight: 20,
                        border: '1px solid transparent',
                        borderRadius: 1,
                        p: 0.5,
                        cursor: 'text',
                        fontStyle: 'italic',
                        '&:focus': { borderColor: '#e2e8f0' },
                        '& p': { margin: '0 0 6px 0' },
                        '& ul': { pl: 3, mt: 0.5, mb: 1, listStyleType: 'disc', listStylePosition: 'outside' },
                        '& ol': { pl: 3, mt: 0.5, mb: 1, listStyleType: 'decimal', listStylePosition: 'outside' },
                        '& li': { mb: '4px', display: 'list-item' },
                    }}
                />
            )}
        </Box>
    );
};

// ── AI Enhance drawer/popover ─────────────────────────────────────────────────
const EnhancePanel = ({ open, anchorEl, onClose, onEnhance, isLoading }) => {
    const [instruction, setInstruction] = useState('');
    const examples = [
        'Add an executive summary at the top',
        'Make it more concise and business-focused',
        'Add key recommendations section',
        'Use formal report language',
    ];
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
                sx: {
                    width: 360, borderRadius: 2.5,
                    border: '1px solid', borderColor: 'divider',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                px: 2, py: 1.5,
                background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.08))',
                borderBottom: '1px solid rgba(99,102,241,0.15)',
                display: 'flex', alignItems: 'center', gap: 1,
            }}>
                <AutoAwesomeIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>Enhance with AI</Typography>
            </Box>

            <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>
                    Optional: add instructions for the AI
                </Typography>
                <TextField
                    multiline
                    minRows={3}
                    maxRows={5}
                    placeholder="e.g. Make it more executive-friendly, add a summary..."
                    value={instruction}
                    onChange={e => setInstruction(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 1.5 },
                    }}
                />

                {/* Example chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    {examples.map(ex => (
                        <Chip
                            key={ex}
                            label={ex}
                            size="small"
                            variant="outlined"
                            onClick={() => setInstruction(ex)}
                            sx={{
                                fontSize: 10.5, cursor: 'pointer',
                                borderColor: 'rgba(99,102,241,0.3)', color: '#6366f1',
                                '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' },
                            }}
                        />
                    ))}
                </Box>

                <Button
                    variant="contained"
                    fullWidth
                    onClick={() => onEnhance(instruction)}
                    disabled={isLoading}
                    startIcon={isLoading
                        ? <CircularProgress size={14} color="inherit" />
                        : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: 13,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        boxShadow: '0 4px 16px rgba(99,102,241,0.35)',
                        '&:hover': { boxShadow: '0 6px 24px rgba(99,102,241,0.45)' },
                        '&:disabled': { background: 'rgba(99,102,241,0.3)' },
                    }}
                >
                    {isLoading ? 'Enhancing report…' : 'Enhance Report'}
                </Button>

                <Typography sx={{ fontSize: 10.5, color: '#94a3b8', mt: 1, textAlign: 'center' }}>
                    {/* v1: returns all-at-once. Streaming planned for a future update. */}
                    AI will rewrite narrative text — charts are preserved as-is.
                </Typography>
            </Box>
        </Popover>
    );
};

// ── Main ReportCanvas ─────────────────────────────────────────────────────────
const ReportCanvas = ({
    items: initialItems,
    reportTitle: initialTitle,
    sessionId,
    token,
    onBack,
    onOpenPreview,
}) => {
    const [sections, setSections] = useState(() =>
        initialItems.map(item => ({ ...item }))
    );
    const [reportTitle, setReportTitle] = useState(initialTitle || 'AI Insight Report');
    const [editingTitle, setEditingTitle] = useState(false);
    const [showMeta, setShowMeta] = useState(true);

    // Export state
    const [animationDisabled, setAnimationDisabled] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isExportingCSV, setIsExportingCSV] = useState(false);
    const [isExportingHTML, setIsExportingHTML] = useState(false);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Enhance state
    const [enhanceAnchor, setEnhanceAnchor] = useState(null);
    const [isEnhancing, setIsEnhancing] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const canvasRef = useRef(null);
    const titleInputRef = useRef(null);

    // Persist canvas edits to localStorage
    const persistKey = sessionId ? `ai_insight_canvas_${sessionId}` : null;
    useEffect(() => {
        if (!persistKey) return;
        const handler = setTimeout(() => {
            try {
                localStorage.setItem(persistKey, JSON.stringify({ sections, reportTitle }));
            } catch { /* ignore quota errors */ }
        }, 500); // debounce 500ms
        return () => clearTimeout(handler);
    }, [sections, reportTitle, persistKey]);

    // Drag-reorder
    const handleReorder = useCallback((from, to) => {
        setSections(prev => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    }, []);
    const { onDragStart, onDragEnter, onDragEnd } = useSectionDrag(handleReorder);

    // Section CRUD
    const handleRemove = useCallback((idx) => {
        setSections(prev => prev.filter((_, i) => i !== idx));
    }, []);

    const handleUpdateNarrative = useCallback((idx, html) => {
        setSections(prev => prev.map((s, i) => i === idx ? { ...s, narrative: html } : s));
    }, []);

    const handleUpdateTitle = useCallback((idx, newTitle) => {
        setSections(prev => prev.map((s, i) => i === idx ? { ...s, title: newTitle } : s));
    }, []);

    const handleUpdateWidth = useCallback((idx, newWidth) => {
        setSections(prev => prev.map((s, i) => i === idx ? { ...s, layout_width: newWidth } : s));
    }, []);

    // ── AI Enhance ──────────────────────────────────────────────────────────
    const handleEnhance = async (instruction) => {
        setIsEnhancing(true);
        setEnhanceAnchor(null);
        try {
            const payload = {
                items: sections.map(s => ({
                    id: s.id,
                    title: s.title,
                    narrative: stripTags(s.narrative || ''),
                    is_chart: !!s.viz_config_json,
                })),
                instruction: instruction || null,
            };
            const result = await apiFetch(
                `/aiinsight/sessions/${sessionId}/enhance-report`,
                token,
                { method: 'POST', body: JSON.stringify(payload) }
            );
            if (result?.items) {
                setSections(prev => {
                    const prevLookup = prev.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
                    const next = [];
                    const handled = new Set();

                    for (const r of result.items) {
                        if (r.id && prevLookup[r.id]) {
                            const original = prevLookup[r.id];
                            next.push({
                                ...original,
                                title: r.title || original.title,
                                narrative: r.narrative !== undefined ? r.narrative : original.narrative,
                            });
                            handled.add(r.id);
                        }
                    }

                    // Fallback to preserve items safely ignored/dropped
                    for (const s of prev) {
                        if (!handled.has(s.id)) next.push(s);
                    }

                    return next;
                });
                setSnackbar({ open: true, message: '✨ Report enhanced by AI!', severity: 'success' });
            }
        } catch (err) {
            console.error('[ReportCanvas] enhance error:', err);
            setSnackbar({ open: true, message: 'AI enhancement failed. Sections unchanged.', severity: 'error' });
        } finally {
            setIsEnhancing(false);
        }
    };

    // ── PDF Export ──────────────────────────────────────────────────────────
    const handleDownloadPDF = async () => {
        if (!canvasRef.current || sections.length === 0) return;
        // Step 1: disable animations and toggle chrome OFF synchronously
        flushSync(() => {
            setAnimationDisabled(true);
            setIsExportingPDF(true);
            setExportMenuAnchor(null);
        });
        await doubleRAF();

        const el = canvasRef.current;
        const replacedPairs = [];

        try {
            const CAPTURE_WIDTH = 900;
            const A4_W = 210, A4_H = 297, MARGIN = 12;
            const pdfPageHeightPx = CAPTURE_WIDTH * ((A4_H - MARGIN * 2) / (A4_W - MARGIN * 2));

            el.style.width = `${CAPTURE_WIDTH}px`;
            el.style.minWidth = `${CAPTURE_WIDTH}px`;
            el.style.maxWidth = `${CAPTURE_WIDTH}px`;

            await doubleRAF();

            // --- Page boundary protection logic ---
            // Nudge entire sections down if they straddle a page break
            const sectionNodes = Array.from(el.querySelectorAll('.pdf-page-guard'));
            const originalMargins = new Map();

            // Evaluate sequentially since each top margin shift pushes subsequent items down
            for (const node of sectionNodes) {
                const elRect = el.getBoundingClientRect();
                const nodeRect = node.getBoundingClientRect();

                const top = nodeRect.top - elRect.top;
                const bottom = top + nodeRect.height;

                const startPage = Math.floor((top + 1) / pdfPageHeightPx);
                const endPage = Math.floor((bottom - 1) / pdfPageHeightPx);

                // If the element crosses a page boundary AND it's smaller than a full page
                if (startPage !== endPage && nodeRect.height < pdfPageHeightPx) {
                    const targetTop = endPage * pdfPageHeightPx;
                    const shift = targetTop - top;

                    originalMargins.set(node, node.style.marginTop);
                    const currentMargin = parseFloat(window.getComputedStyle(node).marginTop) || 0;

                    // Add shift + 24px buffer to ensure it doesn't hug the cut line
                    node.style.marginTop = `${currentMargin + shift + 24}px`;
                    // Force synchronous reflow so next iteration measures correctly
                    el.getBoundingClientRect();
                }
            }

            await doubleRAF();

            // Step 2: capture each chart individually
            const chartDivs = Array.from(el.querySelectorAll('[data-ai-chart="true"]'));
            for (const chartDiv of chartDivs) {
                try {
                    const rect = chartDiv.getBoundingClientRect();
                    const chartCanvas = await html2canvas(chartDiv, {
                        scale: 2,
                        useCORS: true,
                        allowTaint: true,
                        logging: false,
                        backgroundColor: '#ffffff',
                        width: Math.min(rect.width || CAPTURE_WIDTH, CAPTURE_WIDTH),
                        height: rect.height || 280,
                    });
                    const pngUrl = chartCanvas.toDataURL('image/png');
                    const img = document.createElement('img');
                    img.src = pngUrl;
                    img.style.width = `${rect.width || CAPTURE_WIDTH}px`;
                    img.style.height = `${rect.height || 280}px`;
                    img.style.display = 'block';
                    chartDiv.parentNode.insertBefore(img, chartDiv);
                    chartDiv.style.display = 'none';
                    replacedPairs.push({ chartDiv, img });
                } catch (e) {
                    console.warn('[PDF] chart capture failed:', e);
                }
            }

            // Step 3: capture full canvas
            const canvas = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                scrollX: 0,
                scrollY: 0,
                width: CAPTURE_WIDTH,
                height: el.scrollHeight,
                windowWidth: CAPTURE_WIDTH,
                backgroundColor: '#ffffff',
            });

            // Restore
            replacedPairs.forEach(({ chartDiv, img }) => {
                chartDiv.style.display = '';
                img.parentNode?.removeChild(img);
            });
            el.style.width = '';
            el.style.minWidth = '';
            el.style.maxWidth = '';

            // Step 4: build PDF
            const imgDataStr = canvas.toDataURL('image/png');
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

            const cw = A4_W - MARGIN * 2;
            const ratio = cw / canvas.width;
            const pageH_mm = A4_H - MARGIN * 2;
            const pageH_px = Math.floor(pageH_mm / ratio);

            // Dynamically scan pixels for pure white horizontal "cut lines"
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            const isRowBlank = new Uint8Array(canvas.height);
            for (let y = 0; y < canvas.height; y++) {
                let blank = 1;
                const offset = y * canvas.width * 4;
                for (let x = 0; x < canvas.width; x++) {
                    const idx = offset + x * 4;
                    // tolerance allows slight antialiasing bleeding
                    if (pixels[idx] < 250 || pixels[idx + 1] < 250 || pixels[idx + 2] < 250) {
                        blank = 0;
                        break;
                    }
                }
                isRowBlank[y] = blank;
            }

            let currentY = 0;
            let page = 0;

            while (currentY < canvas.height) {
                if (page > 0) pdf.addPage();

                let sliceH_px = Math.min(canvas.height - currentY, pageH_px);

                // If element overflows, step back until we hit a block of pure white gap
                if (currentY + pageH_px < canvas.height) {
                    let nextY = currentY + pageH_px;
                    let foundGap = false;
                    // search upper 60% of the page
                    for (let y = nextY; y > currentY + (pageH_px * 0.4); y--) {
                        let solid = true;
                        for (let j = 0; j < 6; j++) {
                            if (!isRowBlank[y - j]) { solid = false; break; }
                        }
                        if (solid) {
                            sliceH_px = y - currentY;
                            foundGap = true;
                            break;
                        }
                    }
                    if (!foundGap && page > 0) {
                        console.warn('[PDF] No layout gap found, hard break applied.');
                    }
                }

                // Append full image but shifted up by currentY
                pdf.addImage(imgDataStr, 'PNG', MARGIN, MARGIN - (currentY * ratio), cw, canvas.height * ratio);

                // Mask regions bleeding past the slice cut and bleed top overlapping
                const visibleH_mm = sliceH_px * ratio;
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, MARGIN + visibleH_mm, A4_W, A4_H, 'F'); // Bottom
                pdf.rect(0, 0, A4_W, MARGIN, 'F'); // Top

                currentY += sliceH_px;
                page++;
            }
            pdf.save(`${(reportTitle || 'AI-Insight-Report').replace(/\s+/g, '-')}.pdf`);
            setSnackbar({ open: true, message: 'PDF downloaded!', severity: 'success' });
        } catch (err) {
            console.error('[PDF] error:', err);
            replacedPairs.forEach(({ chartDiv, img }) => {
                chartDiv.style.display = '';
                img.parentNode?.removeChild(img);
            });
            if (el) {
                el.style.width = '';
                el.style.minWidth = '';
                el.style.maxWidth = '';
            }
            setSnackbar({ open: true, message: 'PDF export failed. Please try again.', severity: 'error' });
        } finally {
            setAnimationDisabled(false);
            setIsExportingPDF(false);
        }
    };

    // ── Excel Export ──────────────────────────────────────────────────────────
    const handleExportExcel = async () => {
        flushSync(() => {
            setIsExportingCSV(true);
            setExportMenuAnchor(null);
        });
        try {
            const wb = new ExcelJS.Workbook();
            wb.creator = 'AIDAS - AI Insight Engine';

            // Tab 1: Raw Data
            const wsData = wb.addWorksheet('Report Data');
            sections.forEach((s, idx) => {
                wsData.addRow([]);
                const headerRow = wsData.addRow([`Section ${idx + 1}: ${s.title || 'Untitled'}`]);
                headerRow.font = { bold: true, size: 14 };

                if (s.result_json?.length > 0) {
                    const cols = Object.keys(s.result_json[0]);
                    const header = wsData.addRow(cols);
                    header.font = { bold: true };
                    s.result_json.forEach(row => wsData.addRow(cols.map(c => row[c])));
                } else if (s.narrative) {
                    wsData.addRow([stripTags(s.narrative || '')]);
                }
            });

            // Tab 2: Charts
            const wsCharts = wb.addWorksheet('Charts');
            wsCharts.addRow(['Report Visualizations']).font = { bold: true, size: 16 };

            // Capture Charts
            setAnimationDisabled(true);
            await doubleRAF();

            let rowOffset = 3;
            const chartDivs = canvasRef.current ? Array.from(canvasRef.current.querySelectorAll('[data-ai-chart="true"]')) : [];
            for (let i = 0; i < chartDivs.length; i++) {
                try {
                    const chartCanvas = await html2canvas(chartDivs[i], {
                        scale: 2, useCORS: true, allowTaint: true, logging: false, backgroundColor: '#ffffff',
                    });
                    const imgData = chartCanvas.toDataURL('image/png');
                    const imageId = wb.addImage({
                        base64: imgData,
                        extension: 'png',
                    });
                    wsCharts.addImage(imageId, {
                        tl: { col: 1, row: rowOffset },
                        ext: { width: 600, height: 350 },
                    });
                    rowOffset += 20; // Move down for the next image
                } catch (e) {
                    console.error('[Excel] chart capture failed', e);
                }
            }

            setAnimationDisabled(false);

            const buffer = await wb.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(reportTitle || 'Report').replace(/\s+/g, '_')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);

            setSnackbar({ open: true, message: 'Excel downloaded!', severity: 'success' });
        } catch (e) {
            console.error('[Excel]', e);
            setSnackbar({ open: true, message: 'Export failed.', severity: 'error' });
        } finally {
            setIsExportingCSV(false);
            setAnimationDisabled(false);
        }
    };

    // ── Chartifact HTML Export ──────────────────────────────────────────────
    const handleExportHTML = async () => {
        setIsExportingHTML(true);
        setExportMenuAnchor(null);

        // Capture chart images
        setAnimationDisabled(true);
        await doubleRAF();

        const chartImages = {};
        const chartWrappers = canvasRef.current
            ? Array.from(canvasRef.current.querySelectorAll('[data-ai-chart="true"]'))
            : [];

        for (let i = 0; i < chartWrappers.length; i++) {
            try {
                const c = await html2canvas(chartWrappers[i], {
                    scale: 2, useCORS: true, allowTaint: true,
                    logging: false, backgroundColor: '#ffffff',
                });
                chartImages[i] = c.toDataURL('image/png');
            } catch { chartImages[i] = null; }
        }

        setAnimationDisabled(false);

        const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        let chartIdx = 0;
        const sectionsHTML = sections.map((s, idx) => {
            const isChart = !!s.viz_config_json;
            const narrative = stripTags(s.narrative || '');
            let chartImg = '';
            if (isChart) {
                const img64 = chartImages[chartIdx++];
                if (img64) chartImg = `<img src="${img64}" style="max-width:100%;border-radius:8px;margin-top:12px;" alt="${s.title}" />`;
            }
            return `
      <section class="report-section" id="section-${idx}">
        <h2>${s.title || 'Untitled'}</h2>
        ${isChart ? chartImg : `<div class="narrative">${narrative}</div>`}
        ${isChart && narrative ? `<p class="chart-note">${narrative}</p>` : ''}
        ${s.result_json?.length > 0 ? `<p class="row-count">${s.result_json.length} rows of data</p>` : ''}
      </section>`;
        }).join('\n');

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" /><meta name="viewport" content="width=device-width,initial-scale=1.0" />
<title>${reportTitle}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  :root{--indigo:#6366f1;--slate:#0f172a;--muted:#64748b;--border:#e2e8f0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#ffffff;color:#1e293b;display:block;min-height:100vh;padding:48px 24px}
  .report-wrapper{max-width:820px;margin:0 auto;padding:0;background:#ffffff;border-radius:0;box-shadow:none}
  .report-header{margin-bottom:40px;padding-bottom:24px;border-bottom:3px solid var(--indigo)}
  .report-header h1{font-size:26px;font-weight:800;color:var(--slate);letter-spacing:-0.025em;line-height:1.2;margin-bottom:6px}
  .report-header .meta{font-size:12px;color:var(--muted)}
  .report-section{margin-bottom:32px;}
  h2{font-size:18px;font-weight:700;color:var(--slate);margin-bottom:12px;letter-spacing:-0.01em}
  .narrative{font-size:14px;line-height:1.85;color:#334155}
  .chart-note{font-size:12px;color:var(--muted);font-style:italic;margin-top:10px}
  .row-count{font-size:11px;color:#94a3b8;margin-top:8px}
  footer{display:flex;align-items:center;justify-content:center;gap:6px;color:#cbd5e1;font-size:10.5px;padding-top:24px;border-top:1px solid var(--border);margin-top:32px}
  footer img{width:14px;height:14px;filter:grayscale(100%) opacity(0.5)}
</style>
</head>
<body>
<div class="report-wrapper">
  <div class="report-header">
    <h1>${reportTitle}</h1>
    ${showMeta ? `<p class="meta">Generated ${today} · ${sections.length} section${sections.length !== 1 ? 's' : ''}</p>` : ''}
  </div>
  ${sectionsHTML}
  <footer>
    <img src="/favicon-32x32.png" alt="AIDAS" />
    <span>Generated by AIDAS - AI Insight</span>
  </footer>
</div>
</body>
</html>`;

        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(reportTitle || 'Report').replace(/\s+/g, '_')}.html`;
        a.click();
        URL.revokeObjectURL(url);
        setIsExportingHTML(false);
        setSnackbar({ open: true, message: 'Chartifact HTML downloaded!', severity: 'success' });
    };

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const isExporting = isExportingPDF || isExportingCSV || isExportingHTML;

    return (
        <Box sx={{
            position: 'absolute', inset: 0,
            // Must stay BELOW the app's global navigation layer (MUI AppBar = z-index 1100,
            // PlatformLayout header = above this canvas). Using 100 is enough to cover the
            // report builder sidebar beneath it while leaving the top nav bar accessible.
            zIndex: 100,
            display: 'flex', flexDirection: 'column',
            bgcolor: '#f1f5f9',
            animation: 'slideInRight 0.32s cubic-bezier(0.22,1,0.36,1)',
            '@keyframes slideInRight': {
                from: { transform: 'translateX(100%)', opacity: 0 },
                to: { transform: 'translateX(0)', opacity: 1 },
            },
        }}>

            {/* ── Export/Enhance loading overlay ── */}
            {(isExporting || isEnhancing) && (
                <Backdrop open sx={{ zIndex: 500, bgcolor: 'rgba(15,23,42,0.55)', flexDirection: 'column', gap: 2 }}>
                    <CircularProgress size={48} sx={{ color: '#818cf8' }} thickness={3} />
                    <Typography sx={{ color: '#e2e8f0', fontSize: 15, fontWeight: 600 }}>
                        {isExportingPDF && 'Generating PDF…'}
                        {isExportingHTML && 'Building Chartifact…'}
                        {isExportingCSV && 'Preparing Excel…'}
                        {isEnhancing && '✨ AI is enhancing your report…'}
                    </Typography>
                    {isEnhancing && (
                        <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
                            Narratives are being rewritten — charts are preserved
                        </Typography>
                    )}
                    <LinearProgress
                        sx={{ width: 240, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#818cf8' } }}
                    />
                </Backdrop>
            )}

            {/* ── Top toolbar ── */}
            <Box sx={{
                height: 56, flexShrink: 0,
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2.5,
                bgcolor: '#ffffff',
                borderBottom: '1px solid #e2e8f0',
                boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                zIndex: 10,
            }}>
                {/* Back */}
                <Tooltip title="Back to chat">
                    <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary', '&:hover': { color: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' } }}>
                        <ArrowBackIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                {/* Report title */}
                {editingTitle ? (
                    <input
                        ref={titleInputRef}
                        value={reportTitle}
                        onChange={e => setReportTitle(e.target.value)}
                        onBlur={() => setEditingTitle(false)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditingTitle(false); }}
                        autoFocus
                        style={{
                            fontSize: 16, fontWeight: 700, fontFamily: 'inherit',
                            border: 'none', borderBottom: '2px solid #6366f1',
                            outline: 'none', background: 'transparent',
                            color: '#0f172a', minWidth: 200, maxWidth: 400,
                        }}
                    />
                ) : (
                    <Typography
                        onDoubleClick={() => setEditingTitle(true)}
                        title="Double-click to rename"
                        sx={{
                            fontSize: 16, fontWeight: 700, color: '#0f172a',
                            cursor: 'text', flex: 1, minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            '&:hover': { color: '#4f46e5' },
                            transition: 'color 0.15s',
                        }}
                    >
                        {reportTitle}
                    </Typography>
                )}

                <Box sx={{ flex: 1 }} />

                {/* Section count */}
                <Chip
                    label={`${sections.length} section${sections.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ height: 20, fontSize: 10.5, bgcolor: 'rgba(99,102,241,0.1)', color: '#6366f1', border: 'none' }}
                />

                {/* ✨ Enhance with AI */}
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 15 }} />}
                    onClick={e => setEnhanceAnchor(e.currentTarget)}
                    disabled={isEnhancing || sections.length === 0}
                    sx={{
                        textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                        borderRadius: 2, px: 1.75,
                        borderColor: 'rgba(99,102,241,0.5)',
                        color: '#6366f1',
                        background: 'linear-gradient(135deg, rgba(99,102,241,0.06), rgba(139,92,246,0.04))',
                        '&:hover': {
                            borderColor: '#6366f1',
                            background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))',
                        },
                    }}
                >
                    Enhance with AI
                </Button>

                {/* Export dropdown */}
                <Button
                    variant="contained"
                    size="small"
                    endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                    onClick={e => setExportMenuAnchor(e.currentTarget)}
                    disabled={sections.length === 0 || isExporting}
                    sx={{
                        textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                        borderRadius: 2, px: 1.75,
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        boxShadow: '0 2px 12px rgba(99,102,241,0.3)',
                        '&:hover': { boxShadow: '0 4px 20px rgba(99,102,241,0.45)' },
                    }}
                >
                    {isExporting ? <CircularProgress size={14} color="inherit" /> : 'Export'}
                </Button>
            </Box>

            {/* Export Menu */}
            <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                TransitionComponent={Fade}
                PaperProps={{
                    sx: {
                        mt: 0.75, borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                        border: '1px solid', borderColor: 'divider',
                        minWidth: 200,
                    }
                }}
            >
                <MenuItem onClick={handleDownloadPDF} disabled={isExportingPDF}>
                    <ListItemIcon><PictureAsPdfIcon sx={{ fontSize: 18, color: '#f43f5e' }} /></ListItemIcon>
                    <ListItemText primary="Download PDF" primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
                <MenuItem onClick={handleExportExcel} disabled={isExportingCSV}>
                    <ListItemIcon><TableChartIcon sx={{ fontSize: 18, color: '#10b981' }} /></ListItemIcon>
                    <ListItemText primary="Download Excel (.xlsx)" secondary="Data & charts across tabs" primaryTypographyProps={{ fontSize: 13 }} secondaryTypographyProps={{ fontSize: 10.5 }} />
                </MenuItem>
                <MenuItem onClick={handleExportHTML} disabled={isExportingHTML}>
                    <ListItemIcon><CodeIcon sx={{ fontSize: 18, color: '#6366f1' }} /></ListItemIcon>
                    <ListItemText
                        primary="Download Chartifact"
                        secondary="Self-contained HTML webapp"
                        primaryTypographyProps={{ fontSize: 13 }}
                        secondaryTypographyProps={{ fontSize: 10.5 }}
                    />
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setExportMenuAnchor(null); onOpenPreview?.(sections, reportTitle); }}>
                    <ListItemIcon><PreviewIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></ListItemIcon>
                    <ListItemText primary="Preview" primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
            </Menu>

            {/* Enhance popover */}
            <EnhancePanel
                open={Boolean(enhanceAnchor)}
                anchorEl={enhanceAnchor}
                onClose={() => setEnhanceAnchor(null)}
                onEnhance={handleEnhance}
                isLoading={isEnhancing}
            />

            {/* ── Main content area ── */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* Left sidebar — section navigator */}
                <Box sx={{
                    width: 230, flexShrink: 0,
                    bgcolor: '#ffffff',
                    borderRight: '1px solid #e2e8f0',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                            Sections
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: 1, overflowY: 'auto', px: 1.5, pb: 2,
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 2 },
                    }}>
                        {sections.map((s, idx) => (
                            <Box
                                key={s.id || idx}
                                onClick={() => {
                                    document.getElementById(buildSectionId(s))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    px: 1.5, py: 1, borderRadius: 1.5, mb: 0.5,
                                    cursor: 'pointer',
                                    transition: 'all 0.12s',
                                    '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' },
                                }}
                            >
                                <Typography sx={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, flexShrink: 0, minWidth: 18 }}>
                                    {String(idx + 1).padStart(2, '0')}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: 12, color: '#334155', flex: 1 }}>
                                    {s.title || `Section ${idx + 1}`}
                                </Typography>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                                    bgcolor: s.viz_config_json ? '#06b6d4' : '#a78bfa',
                                }} />
                            </Box>
                        ))}
                        {sections.length === 0 && (
                            <Typography sx={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', mt: 3 }}>
                                No sections
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Canvas / document area */}
                <Box sx={{
                    flex: 1, overflowY: 'auto',
                    px: { xs: 2, md: 4 }, py: 4,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.12)', borderRadius: 3 },
                }}>
                    {/* A4-like document paper */}
                    <Box
                        ref={canvasRef}
                        sx={{
                            maxWidth: 820, mx: 'auto',
                            bgcolor: '#ffffff',
                            borderRadius: 2,
                            boxShadow: '0 4px 32px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
                            px: { xs: 3, sm: 5 },
                            py: 5,
                        }}
                    >
                        {/* Report header */}
                        <Box sx={{ mb: 4, pb: 3, borderBottom: '3px solid #6366f1', position: 'relative', '&:hover .meta-remove': { opacity: 1 } }}>
                            <TextareaAutosize
                                value={reportTitle}
                                onChange={e => setReportTitle(e.target.value)}
                                placeholder="Enter report title..."
                                style={{
                                    width: '100%', fontSize: 26, fontWeight: 800, fontFamily: 'inherit',
                                    letterSpacing: '-0.025em', lineHeight: 1.2, color: '#0f172a',
                                    border: 'none', borderBottom: '2px solid transparent',
                                    outline: 'none', background: 'transparent', padding: '0 0 4px 0',
                                    marginBottom: '6px',
                                    transition: 'border-color 0.2s', resize: 'none', overflow: 'hidden'
                                }}
                                onFocus={e => e.target.style.borderBottom = '2px solid #6366f1'}
                                onBlur={e => e.target.style.borderBottom = '2px solid transparent'}
                            />
                            {showMeta && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
                                        Generated {today} · {sections.length} section{sections.length !== 1 ? 's' : ''}
                                    </Typography>
                                    {!isExporting && (
                                        <IconButton className="meta-remove" size="small" onClick={() => setShowMeta(false)} sx={{ opacity: 0, p: 0, transition: 'opacity 0.2s', color: 'text.disabled', '&:hover': { color: '#f43f5e' } }}>
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Sections */}
                        {sections.length === 0 ? (
                            <Box sx={{ py: 8, textAlign: 'center', opacity: 0.5 }}>
                                <Typography sx={{ color: '#94a3b8', fontSize: 14 }}>
                                    All sections removed. Go back and re-select items.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -2, rowGap: 4 }}>
                                {sections.map((s, idx) => (
                                    <Box key={s.id || idx} sx={{ width: s.layout_width === 'half' ? '50%' : '100%', px: 2 }}>
                                        <CanvasSection
                                            item={s}
                                            idx={idx}
                                            totalCount={sections.length}
                                            animationDisabled={animationDisabled}
                                            onDragStart={onDragStart}
                                            onDragEnter={onDragEnter}
                                            onDragEnd={onDragEnd}
                                            onRemove={handleRemove}
                                            onUpdateNarrative={handleUpdateNarrative}
                                            onUpdateTitle={handleUpdateTitle}
                                            onUpdateWidth={handleUpdateWidth}
                                            isEnhancing={isEnhancing}
                                            isExporting={isExporting}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Report footer */}
                        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                            <img src="/favicon-32x32.png" alt="AIDAS" style={{ width: 14, height: 14, filter: 'grayscale(100%) opacity(0.5)' }} />
                            <Typography sx={{ fontSize: 10.5, color: '#cbd5e1' }}>
                                Generated by AIDAS - AI Insight
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReportCanvas;
